import { useT } from "../../../shared/i18n/i18n";
import { AlertTriangle, MoreHorizontal, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import {
  useDashboardWidgets,
  type DashboardWidget,
  type DashboardWidgetConfig,
  type DashboardWidgetData,
} from "../../../entities/dashboard/model/use-dashboard-widgets";
import { type PredicateBuilderState, VisualPredicateBuilder } from "../../board-views/ui/visual-predicate-builder";
import { cn } from "../../../shared/lib/cn";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const numericFieldTypes = new Set(["currency", "duration", "number", "percent", "rating"]);

export type DashboardBoardOption = { id: string; name: string };

function thresholdBreached(value: number, threshold: NonNullable<DashboardWidgetConfig["threshold"]>): boolean {
  switch (threshold.op) {
    case "gt":
      return value > threshold.value;
    case "gte":
      return value >= threshold.value;
    case "lt":
      return value < threshold.value;
    case "lte":
      return value <= threshold.value;
  }
}

// The sprint Report widgets generalized to a standalone dashboard view: each
// widget picks its own source board + filter, so a single dashboard reads
// across the whole workspace (OKR progress, cross-board queues, notes).
export function DashboardWidgets({
  boards,
  fields,
  ownBoardId,
  viewId,
}: {
  boards: DashboardBoardOption[];
  fields: WorkObjectFieldDefinition[];
  ownBoardId: string;
  viewId: string;
}) {
  const t = useT();
  const widgets = useDashboardWidgets(viewId);
  const [editorState, setEditorState] = useState<{ widget: DashboardWidget | null } | null>(null);
  const dataById = useMemo(() => new Map(widgets.data.map((entry) => [entry.widgetId, entry])), [widgets.data]);

  const metricWidgets = widgets.widgets.filter((widget) => widget.kind === "metric");
  const blockWidgets = widgets.widgets.filter((widget) => widget.kind !== "metric");
  const boardNameById = useMemo(() => new Map(boards.map((board) => [board.id, board.name])), [boards]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 py-4">
      {metricWidgets.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {metricWidgets.map((widget) => (
            <MetricWidgetCard
              data={dataById.get(widget.id) ?? null}
              key={widget.id}
              onEdit={() => setEditorState({ widget })}
              onRemove={() => void widgets.removeWidget(widget.id)}
              sourceName={widget.config.boardId ? boardNameById.get(widget.config.boardId) ?? null : null}
              widget={widget}
            />
          ))}
        </div>
      ) : null}

      {blockWidgets.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {blockWidgets.map((widget) =>
            widget.kind === "note" ? (
              <section className="rounded-lg border border-border/70 bg-card p-4" key={widget.id}>
                <WidgetCardHeader onEdit={() => setEditorState({ widget })} onRemove={() => void widgets.removeWidget(widget.id)} title={widget.title} />
                <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-foreground/85">{widget.config.text ?? ""}</p>
              </section>
            ) : (
              <section className="rounded-lg border border-border/70 bg-card p-4" key={widget.id}>
                <WidgetCardHeader
                  badge={dataById.get(widget.id)?.items?.length ?? null}
                  onEdit={() => setEditorState({ widget })}
                  onRemove={() => void widgets.removeWidget(widget.id)}
                  subtitle={widget.config.boardId ? boardNameById.get(widget.config.boardId) ?? null : null}
                  title={widget.title}
                />
                <WidgetItemsList data={dataById.get(widget.id) ?? null} />
              </section>
            ),
          )}
        </div>
      ) : null}

      {widgets.widgets.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t("widgets.empty")}
        </p>
      ) : null}

      <div>
        <Button className="text-muted-foreground" onClick={() => setEditorState({ widget: null })} size="sm" variant="ghost">
          <Plus data-icon="inline-start" />
          {t("widgets.add")}
        </Button>
      </div>

      {editorState ? (
        <WidgetDialog
          boards={boards}
          fields={fields}
          onClose={() => setEditorState(null)}
          onSave={async (input) => {
            if (editorState.widget) {
              await widgets.updateWidget({ config: input.config, title: input.title, widgetId: editorState.widget.id });
            } else {
              await widgets.addWidget(input);
            }
            setEditorState(null);
          }}
          ownBoardId={ownBoardId}
          widget={editorState.widget}
        />
      ) : null}
    </div>
  );
}

function WidgetCardHeader({
  badge,
  onEdit,
  onRemove,
  subtitle,
  title,
}: {
  badge?: number | null;
  onEdit: () => void;
  onRemove: () => void;
  subtitle?: string | null;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <h3 className="min-w-0 flex-1 truncate text-sm font-medium">
        {title}
        {subtitle ? <span className="ml-1.5 text-xs font-normal text-muted-foreground/70">{subtitle}</span> : null}
      </h3>
      {badge !== null && badge !== undefined ? <span className="text-xs tabular-nums text-muted-foreground">{badge}</span> : null}
      <WidgetMenu onEdit={onEdit} onRemove={onRemove} />
    </div>
  );
}

function WidgetMenu({ onEdit, onRemove }: { onEdit: () => void; onRemove: () => void }) {
  const t = useT();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button aria-label={t("widgets.options")} className="size-6 shrink-0 text-muted-foreground" size="icon-sm" variant="ghost">
            <MoreHorizontal className="size-3.5" />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuItem onClick={onEdit}>{t("common.edit")}</DropdownMenuItem>
        <DropdownMenuItem onClick={onRemove} variant="destructive">
          {t("common.delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MetricWidgetCard({
  data,
  onEdit,
  onRemove,
  sourceName,
  widget,
}: {
  data: DashboardWidgetData | null;
  onEdit: () => void;
  onRemove: () => void;
  sourceName: string | null;
  widget: DashboardWidget;
}) {
  const t = useT();
  const threshold = widget.config.threshold ?? null;
  const value = data?.value ?? null;
  const breached = threshold && value !== null ? thresholdBreached(value, threshold) : false;
  return (
    <section className="rounded-lg border border-border/70 bg-card p-4">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={sourceName ?? undefined}>
          {widget.title}
        </p>
        <WidgetMenu onEdit={onEdit} onRemove={onRemove} />
      </div>
      {data?.error ? (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-amber-500" title={data.error}>
          <AlertTriangle className="size-3.5 shrink-0" />
          <span className="truncate">{t("widgets.broken")}</span>
        </div>
      ) : (
        <p
          className={cn(
            "mt-1 text-2xl font-semibold tabular-nums",
            breached && threshold?.tone === "amber" && "text-amber-500",
            breached && threshold?.tone === "red" && "text-red-500",
          )}
        >
          {value ?? "—"}
        </p>
      )}
    </section>
  );
}

function WidgetItemsList({ data }: { data: DashboardWidgetData | null }) {
  const t = useT();
  if (data?.error) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-500" title={data.error}>
        <AlertTriangle className="size-3.5 shrink-0" />
        {t("widgets.brokenEdit")}
      </div>
    );
  }
  const items = data?.items ?? [];
  if (items.length === 0) {
    return <p className="mt-2 text-xs text-muted-foreground">{t("widgets.nothingMatches")}</p>;
  }
  return (
    <div className="mt-2 flex flex-col">
      {items.map((item) => (
        <div className="flex items-center gap-2 rounded-md px-1.5 py-1" key={item.workObjectId}>
          <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: item.statusColor ?? "#64748b" }} />
          <span className="min-w-0 flex-1 truncate text-xs">{item.title}</span>
          {item.typeName ? <span className="shrink-0 text-[11px] text-muted-foreground/70">{item.typeName}</span> : null}
          <span className="shrink-0 text-xs text-muted-foreground">{item.statusName}</span>
        </div>
      ))}
    </div>
  );
}

function WidgetDialog({
  boards,
  fields,
  onClose,
  onSave,
  ownBoardId,
  widget,
}: {
  boards: DashboardBoardOption[];
  fields: WorkObjectFieldDefinition[];
  onClose: () => void;
  onSave: (input: { config: DashboardWidgetConfig; kind: DashboardWidget["kind"]; title: string }) => Promise<void>;
  ownBoardId: string;
  widget: DashboardWidget | null;
}) {
  const t = useT();
  const [kind, setKind] = useState<DashboardWidget["kind"]>(widget?.kind ?? "metric");
  const [title, setTitle] = useState(widget?.title ?? "");
  const [boardId, setBoardId] = useState(widget?.config.boardId ?? ownBoardId);
  const [filterState, setFilterState] = useState<PredicateBuilderState | null>(null);
  const [aggregateOp, setAggregateOp] = useState<"avg" | "count" | "sum">(widget?.config.aggregate?.op ?? "count");
  const [aggregateFieldId, setAggregateFieldId] = useState(widget?.config.aggregate?.fieldDefinitionId ?? "");
  const [thresholdTone, setThresholdTone] = useState<"amber" | "none" | "red">(widget?.config.threshold?.tone ?? "none");
  const [thresholdOp, setThresholdOp] = useState<"gt" | "gte" | "lt" | "lte">(widget?.config.threshold?.op ?? "gt");
  const [thresholdValue, setThresholdValue] = useState(String(widget?.config.threshold?.value ?? 0));
  const [sortFieldId, setSortFieldId] = useState(widget?.config.sortFieldId ?? "");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(widget?.config.sortDirection ?? "asc");
  const [limit, setLimit] = useState(String(widget?.config.limit ?? 10));
  const [text, setText] = useState(widget?.config.text ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numericFields = fields.filter((field) => numericFieldTypes.has(field.type));
  // Незаполненное условие держит кнопку сохранения так же, как неразобранная формула.
  const filterReady = filterState === null || filterState.isEmpty || (filterState.isReady && !filterState.hasIncomplete);
  const canSave =
    title.trim().length > 0 &&
    (kind !== "metric" || aggregateOp === "count" || aggregateFieldId.length > 0) &&
    (kind === "note" || filterReady);

  async function save() {
    setIsSaving(true);
    setError(null);
    try {
      const filterExpression = filterState && !filterState.isEmpty && filterState.isReady ? filterState.expression : null;
      const sourceBoardId = boardId === ownBoardId ? null : boardId;
      const config: DashboardWidgetConfig =
        kind === "note"
          ? { text }
          : kind === "items"
            ? {
                boardId: sourceBoardId,
                filterExpression,
                limit: Math.min(Math.max(Number(limit) || 10, 1), 25),
                sortDirection,
                sortFieldId: sortFieldId || null,
              }
            : {
                aggregate: aggregateOp === "count" ? { op: "count" } : { fieldDefinitionId: aggregateFieldId, op: aggregateOp },
                boardId: sourceBoardId,
                filterExpression,
                threshold:
                  thresholdTone === "none"
                    ? null
                    : { op: thresholdOp, tone: thresholdTone, value: Number(thresholdValue) || 0 },
              };
      await onSave({ config, kind, title: title.trim() });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Could not save the widget");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog onOpenChange={(open) => (!open ? onClose() : null)} open>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{widget ? "Edit widget" : t("widgets.add")}</DialogTitle>
          <DialogDescription>{t("widgets.eachWidget")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {!widget ? (
            <div className="flex items-center gap-0.5 self-start rounded-md bg-muted/50 p-0.5">
              {(
                [
                  { key: "metric", label: t("widgets.metric") },
                  { key: "items", label: t("widgets.itemList") },
                  { key: "note", label: t("widgets.note") },
                ] as const
              ).map((entry) => (
                <button
                  className={cn(
                    "rounded px-2 py-1 text-xs transition-colors",
                    kind === entry.key ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
                  )}
                  key={entry.key}
                  onClick={() => setKind(entry.key)}
                  type="button"
                >
                  {entry.label}
                </button>
              ))}
            </div>
          ) : null}

          <Field>
            <FieldLabel>{t("widgets.title")}</FieldLabel>
            <Input
              onChange={(event) => setTitle(event.target.value)}
              placeholder={kind === "metric" ? "e.g. Q3 objective progress" : kind === "items" ? t("dashboards.widgetTitlePlaceholder") : t("dashboards.widgetSummaryPlaceholder")}
              value={title}
            />
          </Field>

          {kind === "note" ? (
            <Field>
              <FieldLabel>{t("widgets.text")}</FieldLabel>
              <Textarea onChange={(event) => setText(event.target.value)} placeholder={t("widgets.whatMatters")} rows={5} value={text} />
            </Field>
          ) : (
            <>
              <Field>
                <FieldLabel>{t("widgets.board")}</FieldLabel>
                <Select onValueChange={(value) => setBoardId(value ?? ownBoardId)} value={boardId}>
                  <SelectTrigger>
                    <SelectValue>
                      {(value: string) => boards.find((board) => board.id === value)?.name ?? "Pick a board"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {boards.map((board) => (
                      <SelectItem key={board.id} value={board.id}>
                        {board.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>{t("widgets.filter")}</FieldLabel>
                <VisualPredicateBuilder
                  disabled={isSaving}
                  emptyHint={t("filterBuilder.emptyHintBoardWidget")}
                  fields={fields}
                  initialExpression={widget?.config.filterExpression ?? ""}
                  onStateChange={setFilterState}
                  resetSignal={widget?.id ?? "new"}
                />
              </Field>

              {kind === "metric" ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel>{t("widgets.value")}</FieldLabel>
                      <Select onValueChange={(value) => setAggregateOp(value as typeof aggregateOp)} value={aggregateOp}>
                        <SelectTrigger>
                          <SelectValue>
                            {(value: string) => ({ avg: t("widgets.average"), count: t("widgets.count"), sum: t("widgets.sum") })[value]}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="count">{t("widgets.count")}</SelectItem>
                          <SelectItem value="sum">{t("widgets.sum")}</SelectItem>
                          <SelectItem value="avg">{t("widgets.average")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    {aggregateOp !== "count" ? (
                      <Field>
                        <FieldLabel>{t("widgets.field")}</FieldLabel>
                        <Select onValueChange={(value) => setAggregateFieldId(value ?? "")} value={aggregateFieldId}>
                          <SelectTrigger>
                            <SelectValue placeholder={t("widgets.pickField")}>
                              {(value: string) => numericFields.find((field) => field.id === value)?.name ?? t("widgets.pickField")}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {numericFields.map((field) => (
                              <SelectItem key={field.id} value={field.id}>
                                {field.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Field>
                      <FieldLabel>{t("widgets.highlight")}</FieldLabel>
                      <Select onValueChange={(value) => setThresholdTone(value as typeof thresholdTone)} value={thresholdTone}>
                        <SelectTrigger>
                          <SelectValue>
                            {(value: string) => ({ amber: t("widgets.amberWhen"), none: t("widgets.never"), red: t("widgets.redWhen") })[value]}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("widgets.never")}</SelectItem>
                          <SelectItem value="amber">{t("widgets.amberWhen")}</SelectItem>
                          <SelectItem value="red">{t("widgets.redWhen")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    {thresholdTone !== "none" ? (
                      <>
                        <Field>
                          <FieldLabel>{t("widgets.condition")}</FieldLabel>
                          <Select onValueChange={(value) => setThresholdOp(value as typeof thresholdOp)} value={thresholdOp}>
                            <SelectTrigger>
                              <SelectValue>
                                {(value: string) => ({ gt: t("widgets.greaterThan"), gte: t("widgets.atLeast"), lt: t("widgets.lessThan"), lte: t("widgets.atMost") })[value]}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gt">{t("widgets.greaterThan")}</SelectItem>
                              <SelectItem value="gte">{t("widgets.atLeast")}</SelectItem>
                              <SelectItem value="lt">{t("widgets.lessThan")}</SelectItem>
                              <SelectItem value="lte">{t("widgets.atMost")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field>
                          <FieldLabel>{t("widgets.value")}</FieldLabel>
                          <Input inputMode="decimal" onChange={(event) => setThresholdValue(event.target.value)} value={thresholdValue} />
                        </Field>
                      </>
                    ) : null}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <Field>
                    <FieldLabel>{t("widgets.sortBy")}</FieldLabel>
                    <Select onValueChange={(value) => setSortFieldId(!value || value === "__rank__" ? "" : value)} value={sortFieldId || "__rank__"}>
                      <SelectTrigger>
                        <SelectValue>
                          {(value: string) =>
                            value === "__rank__" ? t("widgets.boardOrder") : fields.find((field) => field.id === value)?.name ?? t("widgets.boardOrder")
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__rank__">{t("widgets.boardOrder")}</SelectItem>
                        {fields
                          .filter((field) => field.key !== "title")
                          .map((field) => (
                            <SelectItem key={field.id} value={field.id}>
                              {field.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field>
                    <FieldLabel>{t("widgets.direction")}</FieldLabel>
                    <Select onValueChange={(value) => setSortDirection(value as typeof sortDirection)} value={sortDirection}>
                      <SelectTrigger>
                        <SelectValue>{(value: string) => (value === "asc" ? t("widgets.ascending") : t("widgets.descending"))}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">{t("widgets.ascending")}</SelectItem>
                        <SelectItem value="desc">{t("widgets.descending")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field>
                    <FieldLabel>{t("widgets.showTop")}</FieldLabel>
                    <Input inputMode="numeric" onChange={(event) => setLimit(event.target.value)} value={limit} />
                  </Field>
                </div>
              )}
            </>
          )}

          {error ? <p className="text-xs text-red-500">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            {t("common.cancel")}
          </Button>
          <Button disabled={!canSave || isSaving} onClick={() => void save()}>
            {widget ? "Save" : t("widgets.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
